import { useEffect, useMemo, useState, useCallback } from "react";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
} from "@/components/ui/table";
import {
    Scale,
    CalendarDays,
    Save,
    History,
    AlertTriangle,
    ClipboardList,
    Loader2,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { OPS } from "@/constants/testIds";
import { EmptyState } from "@/components/common/EmptyState";
import { fmtNumber } from "@/lib/format";

const QC = OPS.qc;

// ---------------------------------------------------------------- helpers
const pad = (n) => String(n).padStart(2, "0");
const fmtDateLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayLocal = () => fmtDateLocal(new Date());
const parseLocal = (s) => {
    if (!s) return undefined;
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
};
const slug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "x";
const n3 = (v, lang) => (v === null || v === undefined ? "\u2014" : fmtNumber(v, lang, 3));
const pct1 = (v, lang) => (v === null || v === undefined ? "\u2014" : `${fmtNumber(v, lang, 1)}%`);

// waste -> visual treatment
function wasteKind(waste, pct) {
    if (waste < 0) return "negative";
    if (pct === null || pct === undefined) return "neutral";
    if (pct > 10) return "red";
    if (pct >= 3) return "amber";
    return "green";
}
const KIND_STYLE = {
    red: { color: "#DC2626", bg: "rgba(220,38,38,0.10)" },
    amber: { color: "#B45309", bg: "rgba(180,83,9,0.12)" },
    green: { color: "#16A34A", bg: "rgba(22,163,74,0.12)" },
    neutral: { color: "var(--ink-muted)", bg: "rgba(90,82,77,0.08)" },
    negative: { color: "#B45309", bg: "rgba(180,83,9,0.10)" },
};

function bilingualName(item, lang) {
    const primary = lang === "ar" ? item.name_ar : item.name_en;
    const secondary = lang === "ar" ? item.name_en : item.name_ar;
    return { primary: primary || item.name_en || item.name_ar, secondary };
}

// ---------------------------------------------------------------- waste cell
function WasteCell({ waste, pct, lang, t }) {
    const kind = wasteKind(waste, pct);
    const st = KIND_STYLE[kind];
    return (
        <div className="flex flex-col items-end gap-0.5">
            <span
                className="inline-flex items-center rounded-md px-2 py-0.5 text-sm font-bold tnum"
                style={{
                    color: st.color,
                    backgroundColor: st.bg,
                    border: kind === "negative" ? "1px dashed " + st.color : "none",
                }}
            >
                <bdi dir="ltr">{n3(waste, lang)}</bdi>
            </span>
            {kind === "negative" ? (
                <span className="flex items-center gap-1 text-[11px] font-medium" style={{ color: st.color }}>
                    <AlertTriangle className="h-3 w-3" />
                    {t("qc_check_entry")}
                </span>
            ) : null}
        </div>
    );
}

// ---------------------------------------------------------------- result panel
function ResultPanel({ result, loading, lang, t }) {
    if (loading) {
        return (
            <Card data-testid={QC.result} className="surface border-[color:var(--border-soft)] rounded-2xl shadow-[var(--shadow-sm)]">
                <CardContent className="py-6 space-y-3">
                    <Skeleton className="h-6 w-48" />
                    <Skeleton className="h-[260px] w-full rounded-xl" />
                </CardContent>
            </Card>
        );
    }
    if (!result || result.found === false) {
        return (
            <Card data-testid={QC.result} className="surface border-[color:var(--border-soft)] rounded-2xl shadow-[var(--shadow-sm)]">
                <CardContent className="py-12">
                    <EmptyState
                        testId={QC.emptyState}
                        icon={ClipboardList}
                        title={t("qc_no_result")}
                        dataset="qc_visits"
                    />
                </CardContent>
            </Card>
        );
    }

    if (result.baseline) {
        return (
            <Card data-testid={QC.result} className="surface border-[color:var(--border-soft)] rounded-2xl shadow-[var(--shadow-sm)]">
                <CardContent className="py-6">
                    <div
                        data-testid={QC.resultBaseline}
                        className="flex items-start gap-3 rounded-xl border border-[color:var(--brand-sky)] bg-[color:rgba(186,217,235,0.25)] px-4 py-3"
                    >
                        <CalendarDays className="mt-0.5 h-5 w-5 shrink-0 text-[color:var(--brand-brown)]" />
                        <p className="text-sm font-medium text-[color:var(--ink)]">{t("qc_baseline")}</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    const period = result.period || {};
    const gaps = result.data_gaps || [];
    const rows = result.ingredients || [];

    const summary = rows.reduce(
        (acc, r) => {
            if (r.category === "cheese") acc.cheese += r.waste || 0;
            else if (r.category === "falafel") acc.falafel += r.waste || 0;
            else if (r.category === "drinks") acc.drinks += r.waste || 0;
            return acc;
        },
        { cheese: 0, falafel: 0, drinks: 0 },
    );

    return (
        <Card data-testid={QC.result} className="surface border-[color:var(--border-soft)] rounded-2xl shadow-[var(--shadow-sm)]">
            <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 pb-3">
                <CardTitle className="text-xl font-bold text-[color:var(--ink)]">{t("qc_result_title")}</CardTitle>
                <Badge
                    data-testid={QC.periodLabel}
                    variant="secondary"
                    className="gap-1.5 bg-[color:rgba(209,128,47,0.14)] text-[color:var(--brand-brown)] border-0"
                >
                    <CalendarDays className="h-4 w-4" />
                    <bdi dir="ltr">{period.from} → {period.to}</bdi>
                    <span className="tnum">({fmtNumber(period.days, lang, 0)} {t("qc_days")})</span>
                </Badge>
            </CardHeader>
            <CardContent className="space-y-4">
                {gaps.length ? (
                    <div
                        data-testid={QC.gapsWarning}
                        className="flex items-start gap-2 rounded-xl border px-4 py-3 text-sm"
                        style={{ borderColor: "var(--danger)", backgroundColor: "rgba(180,35,24,0.08)", color: "var(--danger)" }}
                    >
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <span className="font-medium">
                            {t("qc_gaps_warn")} <bdi dir="ltr">{gaps.join(", ")}</bdi>
                        </span>
                    </div>
                ) : null}

                <div className="overflow-x-auto">
                    <Table data-testid={QC.resultTable}>
                        <TableHeader>
                            <TableRow className="hover:bg-transparent">
                                <TableHead className="text-start text-[color:var(--ink-muted)]">{t("qc_ingredient")}</TableHead>
                                <TableHead className="text-end text-[color:var(--ink-muted)]">{t("qc_col_opening")}</TableHead>
                                <TableHead className="text-end text-[color:var(--ink-muted)]">{t("qc_col_received")}</TableHead>
                                <TableHead className="text-end text-[color:var(--ink-muted)]">{t("qc_col_should")}</TableHead>
                                <TableHead className="text-end text-[color:var(--ink-muted)]">{t("qc_col_closing")}</TableHead>
                                <TableHead className="text-end text-[color:var(--ink-muted)]">{t("qc_col_waste")}</TableHead>
                                <TableHead className="text-end text-[color:var(--ink-muted)]">{t("qc_col_waste_pct")}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((r) => {
                                const nm = bilingualName(r, lang);
                                return (
                                    <TableRow key={r.ingredient_key} data-testid={QC.resultRow(slug(r.name_en))}>
                                        <TableCell>
                                            <div className="font-semibold text-[color:var(--ink)]">{nm.primary}</div>
                                            <div className="text-xs text-[color:var(--ink-muted)]">{nm.secondary} · {r.unit}</div>
                                        </TableCell>
                                        <TableCell className="text-end tnum text-[color:var(--ink)]"><bdi dir="ltr">{n3(r.opening, lang)}</bdi></TableCell>
                                        <TableCell className="text-end tnum text-[color:var(--ink)]"><bdi dir="ltr">{n3(r.received, lang)}</bdi></TableCell>
                                        <TableCell className="text-end tnum text-[color:var(--ink)]"><bdi dir="ltr">{n3(r.should_have_used, lang)}</bdi></TableCell>
                                        <TableCell className="text-end tnum text-[color:var(--ink)]"><bdi dir="ltr">{n3(r.closing, lang)}</bdi></TableCell>
                                        <TableCell className="text-end"><div className="flex justify-end"><WasteCell waste={r.waste} pct={r.waste_pct} lang={lang} t={t} /></div></TableCell>
                                        <TableCell className="text-end tnum text-[color:var(--ink-muted)]"><bdi dir="ltr">{pct1(r.waste_pct, lang)}</bdi></TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>

                {/* summary */}
                <div data-testid={QC.summary} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <SummaryCard k="cheese" label={t("qc_summary_cheese")} value={summary.cheese} unit={t("qc_kg")} lang={lang} />
                    <SummaryCard k="falafel" label={t("qc_summary_falafel")} value={summary.falafel} unit={t("qc_kg")} lang={lang} />
                    <SummaryCard k="drinks" label={t("qc_summary_drinks")} value={summary.drinks} unit={t("qc_bottles")} lang={lang} />
                </div>
            </CardContent>
        </Card>
    );
}

function SummaryCard({ k, label, value, unit, lang }) {
    const color = value > 0 ? "#B45309" : value < 0 ? "#DC2626" : "var(--ink)";
    return (
        <div
            data-testid={QC.summaryCard(k)}
            className="rounded-xl border border-[color:var(--border-soft)] surface-2 px-4 py-3"
        >
            <p className="text-xs text-[color:var(--ink-muted)]">{label}</p>
            <p className="kpi-number text-2xl font-extrabold tnum" style={{ color }}>
                <bdi dir="ltr">{n3(value, lang)}</bdi>{" "}
                <span className="text-sm font-medium text-[color:var(--ink-muted)]">{unit}</span>
            </p>
        </div>
    );
}

// ---------------------------------------------------------------- page
export default function QCWastePage({ embedded = false }) {
    const { t, lang } = useI18n();

    const [loadingConfig, setLoadingConfig] = useState(true);
    const [branches, setBranches] = useState([]);
    const [ingredients, setIngredients] = useState([]);

    const [branchId, setBranchId] = useState("");
    const [visitDate, setVisitDate] = useState(todayLocal());
    const [dateOpen, setDateOpen] = useState(false);
    const [readings, setReadings] = useState({});

    const [visits, setVisits] = useState([]);
    const [selectedVisit, setSelectedVisit] = useState("");

    const [saving, setSaving] = useState(false);
    const [result, setResult] = useState(null);
    const [loadingResult, setLoadingResult] = useState(false);

    // initial config: unified branches list (same as Sales / QC Inspection)
    // + the 8 ingredients. We filter out finance-only branches client-side
    // because QC Waste needs a POS id to reconcile against sales.
    useEffect(() => {
        let active = true;
        Promise.all([api.get("/branches"), api.get("/qc/ingredients")])
            .then(([b, i]) => {
                if (!active) return;
                const list = (b.data || []).filter(
                    (x) => x.is_active && !x.finance_only && x.pos_branch_id != null,
                );
                setBranches(list);
                setIngredients(i.data || []);
                if (list.length) setBranchId(list[0].id);
            })
            .catch(() => active && toast.error(t("qc_load_error")))
            .finally(() => active && setLoadingConfig(false));
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // resolve current branch → pos_branch_id (integer, for /qc/visits and
    // /qc/reconcile which are still keyed by POS id server-side).
    const currentBranch = useMemo(() => branches.find((b) => b.id === branchId), [branches, branchId]);
    const currentPosId = currentBranch?.pos_branch_id ?? null;

    // when branch changes -> load its past visits, reset entry + result
    useEffect(() => {
        if (!branchId || currentPosId == null) return;
        setReadings({});
        setResult(null);
        setSelectedVisit("");
        api
            .get(`/qc/visits?branch_id=${currentPosId}`)
            .then((r) => setVisits(r.data || []))
            .catch(() => setVisits([]));
    }, [branchId, currentPosId]);

    const setReading = (key, field, val) =>
        setReadings((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: val } }));

    const loadResult = useCallback(async (bid, date) => {
        setLoadingResult(true);
        try {
            const r = await api.get(`/qc/reconcile?branch_id=${bid}&visit_date=${date}`);
            setResult(r.data);
        } catch (e) {
            setResult(null);
            toast.error(t("qc_load_error"));
        } finally {
            setLoadingResult(false);
        }
    }, [t]);

    const handleSave = async () => {
        if (!branchId || !visitDate) {
            toast.error(t("qc_select_branch_first"));
            return;
        }
        // Send the UUID — server unifies with the same branches used in
        // Sales / Financials / QC Inspection and resolves to pos_branch_id.
        const payload = {
            branch_id: branchId,
            visit_date: visitDate,
            readings: ingredients.map((ing) => ({
                ingredient_key: ing.ingredient_key,
                received: Number(readings[ing.ingredient_key]?.received) || 0,
                weighed: Number(readings[ing.ingredient_key]?.weighed) || 0,
            })),
        };
        setSaving(true);
        try {
            await api.post("/qc/visit-app", payload);
            toast.success(t("qc_save_success"));
            if (currentPosId != null) {
                try {
                    const r = await api.get(`/qc/visits?branch_id=${currentPosId}`);
                    setVisits(r.data || []);
                } catch (_) {}
                await loadResult(currentPosId, visitDate);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("qc_save_error"));
        } finally {
            setSaving(false);
        }
    };

    const handlePickVisit = (date) => {
        setSelectedVisit(date);
        setVisitDate(date);
        const v = visits.find((x) => x.visit_date === date);
        const map = {};
        (v?.readings || []).forEach((r) => {
            map[r.ingredient_key] = { received: r.received ?? "", weighed: r.weighed ?? "" };
        });
        setReadings(map);
        if (currentPosId != null) loadResult(currentPosId, date);
    };

    const branchLabel = (b) => (lang === "ar" ? `${b.name_ar} · ${b.name_en || ""}` : `${b.name_en || b.name_ar} · ${b.name_ar}`);

    return (
        <div data-testid={QC.page} className="fin-scope space-y-5 sm:space-y-6">
            {embedded ? null : (
                <PageHeader
                    icon={Scale}
                    title={t("qc_title")}
                    subtitle={t("qc_subtitle")}
                />
            )}
            {/* PART A — record a visit */}
            <Card className="surface border-[color:var(--border-soft)] rounded-2xl shadow-[var(--shadow-sm)]">
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-bold text-[color:var(--ink)]">{t("qc_record_title")}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                    {loadingConfig ? (
                        <div className="space-y-3">
                            <Skeleton className="h-10 w-full max-w-md" />
                            <Skeleton className="h-[280px] w-full rounded-xl" />
                        </div>
                    ) : (
                        <>
                            {/* controls */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-medium text-[color:var(--ink-muted)]">{t("qc_branch")}</Label>
                                    <Select value={branchId} onValueChange={setBranchId}>
                                        <SelectTrigger data-testid={QC.branchSelect} className="surface">
                                            <SelectValue placeholder={t("qc_branch_ph")} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {branches.map((b) => (
                                                <SelectItem key={b.id} value={b.id}>
                                                    {branchLabel(b)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-1.5">
                                    <Label className="text-xs font-medium text-[color:var(--ink-muted)]">{t("qc_visit_date")}</Label>
                                    <Popover open={dateOpen} onOpenChange={setDateOpen}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                data-testid={QC.datePicker}
                                                variant="outline"
                                                className="w-full justify-start gap-2 surface border-[color:var(--border-soft)] font-normal"
                                            >
                                                <CalendarDays className="h-4 w-4 text-[color:var(--brand-brown)]" />
                                                <bdi dir="ltr">{visitDate || t("qc_pick_date")}</bdi>
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <div data-testid={QC.calendar}>
                                                <Calendar
                                                    mode="single"
                                                    selected={parseLocal(visitDate)}
                                                    onSelect={(d) => {
                                                        if (d) setVisitDate(fmtDateLocal(d));
                                                        setDateOpen(false);
                                                    }}
                                                    initialFocus
                                                />
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                </div>

                                <div className="space-y-1.5">
                                    <Label className="text-xs font-medium text-[color:var(--ink-muted)]">{t("qc_past_visits")}</Label>
                                    <Select value={selectedVisit} onValueChange={handlePickVisit} disabled={!visits.length}>
                                        <SelectTrigger data-testid={QC.pastVisits} className="surface gap-2">
                                            <History className="h-4 w-4 text-[color:var(--brand-brown)]" />
                                            <SelectValue placeholder={visits.length ? t("qc_past_visits_ph") : t("qc_no_visits")} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {visits.map((v) => (
                                                <SelectItem key={v.visit_date} value={v.visit_date}>
                                                    <bdi dir="ltr">{v.visit_date}</bdi>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* readings table */}
                            <div className="overflow-x-auto">
                                <Table data-testid={QC.readingsTable}>
                                    <TableHeader>
                                        <TableRow className="hover:bg-transparent">
                                            <TableHead className="text-start text-[color:var(--ink-muted)]">{t("qc_ingredient")}</TableHead>
                                            <TableHead className="text-start text-[color:var(--ink-muted)]">{t("qc_unit")}</TableHead>
                                            <TableHead className="text-start text-[color:var(--ink-muted)]">{t("qc_received")}</TableHead>
                                            <TableHead className="text-start text-[color:var(--ink-muted)]">{t("qc_weighed")}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {ingredients.map((ing) => {
                                            const nm = bilingualName(ing, lang);
                                            const k = slug(ing.name_en);
                                            const row = readings[ing.ingredient_key] || {};
                                            return (
                                                <TableRow key={ing.ingredient_key}>
                                                    <TableCell>
                                                        <div className="font-semibold text-[color:var(--ink)]">{nm.primary}</div>
                                                        <div className="text-xs text-[color:var(--ink-muted)]">{nm.secondary}</div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="secondary" className="bg-[color:rgba(186,217,235,0.4)] text-[color:var(--brand-brown)] border-0">
                                                            {ing.unit}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input
                                                            data-testid={QC.received(k)}
                                                            type="number"
                                                            min="0"
                                                            step="any"
                                                            dir="ltr"
                                                            inputMode="decimal"
                                                            className="w-32 text-end surface"
                                                            value={row.received ?? ""}
                                                            onChange={(e) => setReading(ing.ingredient_key, "received", e.target.value)}
                                                            placeholder="0"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input
                                                            data-testid={QC.weighed(k)}
                                                            type="number"
                                                            min="0"
                                                            step="any"
                                                            dir="ltr"
                                                            inputMode="decimal"
                                                            className="w-32 text-end surface"
                                                            value={row.weighed ?? ""}
                                                            onChange={(e) => setReading(ing.ingredient_key, "weighed", e.target.value)}
                                                            placeholder="0"
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>

                            <div className="flex justify-end">
                                <Button
                                    data-testid={QC.save}
                                    onClick={handleSave}
                                    disabled={saving || !branchId}
                                    className="gap-2 bg-[color:var(--brand-orange)] text-white hover:bg-[color:var(--brand-orange-hover)]"
                                >
                                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                    {saving ? t("qc_saving") : t("qc_save")}
                                </Button>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* PART B — waste result */}
            <ResultPanel result={result} loading={loadingResult} lang={lang} t={t} />
        </div>
    );
}
